import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useApp } from "../state/AppContext";
import { logSiteClick } from "../lib/siteClick";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trophy, Clock, CheckCircle2, ArrowRight, Play } from "lucide-react";
import { cloudinaryThumb } from "../lib/cloudinary";
import ApplyCTA from "../components/ApplyCTA";

function rankBadge(rank) {
  if (rank === 1) return { bg: "bg-scl-mustard", text: "text-scl-charcoal", label: "1" };
  if (rank === 2) return { bg: "bg-scl-teal", text: "text-white", label: "2" };
  if (rank === 3) return { bg: "bg-scl-brick", text: "text-white", label: "3" };
  return { bg: "bg-white border border-scl-border", text: "text-scl-charcoal", label: `${rank}` };
}

export default function LeaderboardScreen({ defaultTab = "mine" }) {
  const ctx = useApp();
  const { user, setScreen, t } = ctx;
  const [tab, setTab] = useState(defaultTab);
  const [mine, setMine] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [subs, lb] = await Promise.all([
          user ? axios.get(`${API}/submissions/user/${user.id}`) : Promise.resolve({ data: [] }),
          axios.get(`${API}/leaderboard`),
        ]);
        setMine(subs.data);
        setLeaders(lb.data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user, tab]);

  return (
    <div className="min-h-screen bg-scl-cream pt-6 px-6 pb-32" data-testid="leaderboard-screen">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.18em] text-scl-teal font-semibold mb-1">{t("progress_kicker")}</div>
        <h2 className="font-serif text-3xl text-scl-charcoal font-bold leading-tight">{t("progress_title")}</h2>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="w-full h-12 bg-white rounded-full p-1 shadow-warm-sm border border-scl-border">
          <TabsTrigger value="mine" data-testid="tab-mine" className="flex-1 rounded-full data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
            {t("tab_mine")}
          </TabsTrigger>
          <TabsTrigger value="board" data-testid="tab-board" className="flex-1 rounded-full data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
            {t("tab_board")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="mt-5 space-y-3" data-testid="my-list">
          {loading && <div className="text-scl-charcoal/60 text-sm">{t("loading")}</div>}
          {!loading && mine.length === 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-warm-sm text-center">
              <p className="text-scl-charcoal/70 mb-4">{t("empty_mine")}</p>
              <button onClick={() => setScreen("gallery")} className="h-12 px-6 rounded-full bg-scl-coral text-white font-semibold min-h-[44px]" data-testid="empty-browse-btn">
                {t("empty_browse")}
              </button>
            </div>
          )}
          {mine.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl p-4 shadow-warm-sm flex items-center gap-4" data-testid={`submission-${s.id}`}>
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-scl-cream flex items-center justify-center flex-shrink-0 relative">
                {s.media_url ? (
                  s.media_type === "video" || s.media_type?.startsWith("video/") ? (
                    <>
                      <img src={cloudinaryThumb(s.media_url, { w: 128, h: 128 })} alt="thumb" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <Play className="absolute w-5 h-5 text-white drop-shadow" />
                    </>
                  ) : (
                    <img
                      src={s.media_url.startsWith("http") ? cloudinaryThumb(s.media_url, { w: 128, h: 128 }) : `${API}/files/${s.media_url}`}
                      alt="thumb"
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <div className="text-scl-teal text-xs">NO MEDIA</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-[15px] text-scl-charcoal font-bold leading-tight line-clamp-2">{s.challenge_title}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-bold text-scl-mustard-foreground bg-scl-mustard px-2 py-0.5 rounded-full">+{s.points} pts</span>
                  <span className={`text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1 ${s.status === "approved" ? "text-scl-teal" : "text-scl-charcoal/60"}`}>
                    {s.status === "approved" ? <><CheckCircle2 className="w-3 h-3" /> {t("approved")}</> : <><Clock className="w-3 h-3" /> {t("pending")}</>}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="board" className="mt-5" data-testid="board-list">
          <div className="bg-white rounded-2xl p-4 shadow-warm-sm">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-scl-border">
              <Trophy className="w-5 h-5 text-scl-mustard" />
              <div className="font-serif text-lg font-bold text-scl-charcoal">{t("top_10")}</div>
            </div>
            {loading && <div className="text-scl-charcoal/60 text-sm">{t("loading")}</div>}
            <ol className="space-y-3">
              {leaders.map((l) => {
                const rb = rankBadge(l.rank);
                const isMe = user && !l.is_mock && l.display_name.startsWith(user.first_name);
                return (
                  <li key={`${l.rank}-${l.display_name}`} className={`flex items-center gap-3 p-2 rounded-xl ${isMe ? "bg-scl-cream" : ""}`} data-testid={`leader-${l.rank}`}>
                    <div className={`w-8 h-8 rounded-full font-bold flex items-center justify-center text-sm ${rb.bg} ${rb.text}`}>{rb.label}</div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-scl-charcoal ${isMe ? "font-bold" : ""}`}>{l.display_name}{isMe && <span className="ml-1 text-xs text-scl-teal font-semibold">· You</span>}</div>
                    </div>
                    <div className="font-serif text-lg font-bold text-scl-charcoal">{l.points}<span className="text-xs text-scl-charcoal/60 font-sans font-medium ml-1">pts</span></div>
                  </li>
                );
              })}
              {leaders.length === 0 && !loading && <div className="text-scl-charcoal/60 text-sm">{t("be_first")}</div>}
            </ol>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <button onClick={() => setScreen("gallery")} data-testid="spin-again-cta" className="w-full h-14 rounded-full bg-scl-coral text-white font-semibold flex items-center justify-center gap-2 shadow-warm hover:brightness-105 active:scale-[0.98] transition-all min-h-[44px]">
          {t("pick_another")} <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      <div className="mt-6">
        <ApplyCTA />
      </div>

      <footer className="mt-8 text-center">
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_scl-kindness/artifacts/6382qm3g_SCL-final-logo-clearspace-namelogo-cropped%20%282%29.png"
          alt="School for Community Learning"
          className="h-10 w-auto object-contain mx-auto mb-2 opacity-80"
        />
        <div className="text-xs text-scl-charcoal/60">Indianapolis, IN · <a href="https://schoolforcommunitylearning.org" target="_blank" rel="noreferrer" onClick={() => logSiteClick(ctx, "leaderboard_footer", "https://schoolforcommunitylearning.org")} className="underline underline-offset-2 hover:text-scl-teal">schoolforcommunitylearning.org</a></div>
      </footer>
    </div>
  );
}
